import React, { useEffect, useRef, useState } from 'react';
import { PanelContainer } from '@/components/@IntranetLibrary/Panel/styles';
import { useHomeEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import {
  EventPageAreaName,
  HomeBannerType,
  HomeEventName,
  HomePageAreaType,
} from '@/constant/gtm';
import { Link } from '@mui/material';
import { isRnWebView, RnMessage, sendToRN } from '@/utils/function';
import type { Swiper as SwiperClass } from 'swiper';
import BannerCarousel from '@/components/BannerCarousel';
import { ActionType } from '@/constant/button';
import { EventBanner as EventBannerItem } from '@/types/event';
import { resolveEventBannerLink } from '@/utils/eventBannerLink';

/** 배너가 2건 이상일 때 자동으로 다음 배너로 넘어가는 간격(ms) */
export const AUTO_SLIDE_INTERVAL = 5 * 1000;

type EventBannerProps = {
  banners: EventBannerItem[];
  pageArea?: HomePageAreaType;
  /**
   * 앱(RN 웹뷰)에서 포탈 게시글 링크를 눌렀을 때 — 홈이 쓰는 게시글 모달로 연다.
   * 없으면(PC 홈) 링크 기본 동작(새 탭)으로 둔다.
   */
  onOpenBoard?: (documentId: string) => void;
};

const EventBanner = ({
  banners,
  pageArea = EventPageAreaName.HOME,
  onOpenBoard,
}: EventBannerProps) => {
  const homeEventTracker = useHomeEventTracker(pageArea);
  const swiperRef = useRef<SwiperClass | null>(null);
  const [currPage, setCurrPage] = useState(1);
  // 마우스를 올려 둔 동안에는 자동 전환을 멈춘다.
  const [isMouseOver, setIsMouseOver] = useState(false);

  const isCarousel = banners.length > 1;

  // 배너가 바뀔 때마다 간격을 다시 센다. 좌우 버튼이나 스와이프로 넘긴 직후
  // 직전 배너에 남아 있던 시간 때문에 곧바로 다음 배너로 넘어가지 않도록 한다.
  // (PC 는 버튼을 누르는 동안 마우스가 올라가 있어 이미 멈추지만, 모바일은 그렇지 않다.)
  useEffect(() => {
    if (!isCarousel || isMouseOver) return;

    const timer = setInterval(
      () => swiperRef.current?.slideNext(),
      AUTO_SLIDE_INTERVAL,
    );
    return () => clearInterval(timer);
  }, [isCarousel, isMouseOver, currPage]);

  if (banners.length === 0) return null;

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    banner: EventBannerItem,
  ) => {
    // GA Home - 15번
    homeEventTracker(HomeEventName.clickBanner, {
      banner_type: HomeBannerType.EVENT,
      // 노출용 응답에는 배너명이 없어 배너 고유번호로 구분한다.
      banner_title: banner.id,
    });

    // 앱(RN 웹뷰)에서는 홈 웹뷰 안에서 페이지를 바꾸지 않는다(홈이 사라진다).
    // 포탈 링크는 앱 안에서(게시글 모달 / 앱 모달), 그 밖은 외부로 연다.
    // 무조건 외부로 보내면 iOS 는 Safari 로 빠지고 Android 는 App Link 로 되돌아와 결과가 갈린다.
    if (isRnWebView()) {
      event.preventDefault();
      const target = resolveEventBannerLink(
        banner.link,
        window.location.origin,
      );
      if (target.kind === 'board' && onOpenBoard) {
        onOpenBoard(target.documentId);
        return;
      }
      if (target.kind === 'external') {
        sendToRN(RnMessage.OPEN_APP, { url: target.url });
        return;
      }
      sendToRN(RnMessage.MODAL, {
        tabName: 'board',
        url: target.kind === 'board' ? banner.link : target.url,
      });
    }
  };

  // 캐러셀 안에서는 컨테이너가 2 대 1 비율을 잡아 주므로 슬라이드는 높이를 물려받는다.
  const renderBanner = (
    banner: EventBannerItem,
    sizeStyle: React.CSSProperties,
  ) => {
    // 어드민 입력값이라 따옴표가 섞여 들어와도 인라인 스타일이 깨지지 않게 한다.
    // 타입상 필수 필드지만 응답에서 빠져도 홈 전체 렌더가 죽지 않도록 값이 있을 때만 처리한다.
    const imageUrl = banner.fileUrl?.replace(/"/g, '%22');

    return (
      <Link
        href={banner.link}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          background: imageUrl
            ? `#E3E3E3 url("${imageUrl}") center / cover no-repeat`
            : '#E3E3E3',
          zIndex: 3,
          borderRadius: '8px',
          ...sizeStyle,
        }}
        onClick={(event) => handleClick(event, banner)}
      />
    );
  };

  if (!isCarousel) {
    return (
      <PanelContainer className="card-panel">
        {renderBanner(banners[0], { width: '100%', aspectRatio: '2 / 1' })}
      </PanelContainer>
    );
  }

  // 터치 기기에서도 탭하면 mouseenter 가 합성으로 발생하는데 mouseleave 는 다른 곳을
  // 건드릴 때까지 오지 않는다. 그대로 두면 배너를 한 번 탭한 뒤 자동 전환이 멈춘 채로
  // 남으므로, 실제 마우스일 때만 정지시킨다.
  const handlePointerHover =
    (isEnter: boolean) => (event: React.PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      setIsMouseOver(isEnter);
    };

  const onClickPageNav = (
    _page: number,
    action: ActionType.PREV | ActionType.NEXT,
  ) => {
    if (action === ActionType.PREV) swiperRef.current?.slidePrev();
    if (action === ActionType.NEXT) swiperRef.current?.slideNext();
  };

  return (
    <PanelContainer
      className="card-panel"
      onPointerEnter={handlePointerHover(true)}
      onPointerLeave={handlePointerHover(false)}
    >
      <BannerCarousel
        items={banners}
        swiperRef={swiperRef}
        onSlideChange={(swiper) => setCurrPage(swiper.realIndex + 1)}
        loop
        containerSx={{
          width: '100%',
          aspectRatio: '2 / 1',
          borderRadius: '8px',
        }}
        pagination={{
          totalPage: banners.length,
          currPage,
          onClickPrev: onClickPageNav,
          onClickNext: onClickPageNav,
          isLoop: true,
          designType: 'frosted',
        }}
        renderSlide={(banner) =>
          renderBanner(banner, { width: '100%', height: '100%' })
        }
      />
    </PanelContainer>
  );
};

export default EventBanner;
